<template>
  <div class="deck" :class="{ study: mode === 'study' }">
    <div v-swipe.right="() => swipeCards('right')" v-swipe.left="() => swipeCards('left')" class="flashcard-wrapper">
      <div
        class="flashcard" :class="{ flip: flip && !showLoginOrSignup || formFlip, 'login-signup': showLoginOrSignup }"
        @click="cardFlip"
      >
        <div
          class="question front"
          :class="{ 'login-email': loginEmail, 'forgotten-password': forgottenPassword, forms: showLoginOrSignup }"
        >
          <template v-if="showLoginOrSignup">
            <div class="forms-study">
              <div v-if="!forgottenPassword" class="left-side">
                <h2 class="h5">
                  Log in to resume working on your flashcard deck.
                </h2>
                <p class="or-sign-up">
                  Don’t have an account?
                  <a @click="loginSignupHandler('signup')">
                    Sign Up
                  </a>
                </p>
              </div>
              <slot name="form" />
              <div class="login-signup-terms-mobile">
                <p class="or-sign-up">
                  Don’t have an account?
                  <a @click="loginSignupHandler('signup')">
                    Sign Up
                  </a>
                </p>
                <p class="terms" :class="{ 'login-form': loginEmail }">
                  By continuing, you agree to {{ $config.platformName }}’s
                  <a target="_blank" :href="`${$config.siteUrl}/terms-of-use/`">Terms of Use</a>
                  and
                  <a target="_blank" :href="`${$config.siteUrl}/privacy-policy/`">Privacy Policy</a>
                </p>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="card">
              <CardContent
                :key="activeCard.question"
                :class="{ 'less-height': showFlipMessage }" type="question" :html="activeCard.question"
                place="study"
              />
            </div>
            <div v-if="showFlipMessage" class="first-card-hint">
              <ClickIcon />
              Click <span class="or">or press the Space bar</span> to flip the card.
            </div>
          </template>
        </div>
        <div class="answer back" :class="{ 'signup-email': signupEmail, forms: showLoginOrSignup }">
          <template v-if="showLoginOrSignup">
            <div class="forms-study">
              <div class="left-side">
                <h2 class="h5">
                  Sign up to see the remaining cards in this deck.
                </h2>
                <p class="or-sign-up">
                  Already have an account?
                  <a @click="loginSignupHandler('login')">
                    Log In
                  </a>
                </p>
              </div>
              <slot name="form" />
              <div class="login-signup-terms-mobile">
                <p class="or-sign-up">
                  Already have an account?
                  <a @click="loginSignupHandler('login')">
                    Log In
                  </a>
                </p>
                <p class="terms" :class="{ 'signup-form': signupEmail }">
                  By continuing, you agree to {{ $config.platformName }}’s
                  <a target="_blank" :href="`${$config.siteUrl}/terms-of-use/`">Terms of Use</a>
                  and
                  <a target="_blank" :href="`${$config.siteUrl}/privacy-policy/`">Privacy Policy</a>
                </p>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="card">
              <CardContent
                :key="activeCard.answer" type="answer" :html="activeCard.answer"
                place="study"
              />
            </div>
          </template>
        </div>
      </div>

      <LineProgress class="line-progress" :class="{ disable: showLoginOrSignup }" :width="progress || 0" />
      <div class="progress-actions">
        <div class="group-button">
          <button :disabled="showLoginOrSignup || disableShuffle" class="shuffle" @click="shuffle">
            <ShuffleIcon class="shuffle" />
            <Tooltip v-if="!showLoginOrSignup">
              Shuffle
            </Tooltip>
          </button>
          <button
            v-if="mode === 'study'" :disabled="showLoginOrSignup" class="reset"
            @click="reset"
          >
            <ResetIcon class="reset" />
            <Tooltip v-if="!showLoginOrSignup">
              Reset
            </Tooltip>
          </button>
        </div>

        <span class="cards-number">{{ activeIndex + 1 }}/{{ deckCardsCount }}</span>
      </div>

      <div v-if="mode === 'review'" class="review-switch">
        <button
          :disabled="showLoginOrSignup || disableLeftArrow" class="btn-secondary caret-left"
          @click="switchCards('left')"
        >
          <ArrowIcon />
          <Tooltip v-if="$store.state.layout === 'desktop'">
            <p>Press</p>
            <LeftRightArrow />
          </Tooltip>
        </button>
        <button
          :disabled="showLoginOrSignup || disableRightArrow" class="btn-secondary caret-right"
          @click="switchCards('right')"
        >
          <ArrowIcon />
          <Tooltip v-if="$store.state.layout === 'desktop'">
            <p>Press</p>
            <LeftRightArrow />
          </Tooltip>
        </button>
      </div>
    </div>
    <div v-if="mode === 'study'" class="flashcard-actions">
      <p>How well do you know this?</p>
      <div class="group-button">
        <button :disabled="submitting || showLoginOrSignup" class="btn-danger-light" @click="nextQuestion(1)">
          Not Well
          <Tooltip v-if="!showLoginOrSignup">
            <p>Press</p>
            <p>1</p>
          </Tooltip>
        </button>
        <button :disabled="submitting || showLoginOrSignup" class="btn-light-shade" @click="nextQuestion(2)">
          Not Sure
          <Tooltip v-if="!showLoginOrSignup">
            <p>Press</p>
            <p>2</p>
          </Tooltip>
        </button>
        <button :disabled="submitting || showLoginOrSignup" class="btn-primary-light" @click="nextQuestion(3)">
          Very Well
          <Tooltip v-if="!showLoginOrSignup">
            <p>Press</p>
            <p>3</p>
          </Tooltip>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import cookies from 'js-cookie';
import CardContent from './CardContent';
import Tooltip from '../components/Tooltip';
import LineProgress from '../LineProgress';
import ShuffleIcon from '../assets/svg/icon-shuffle.svg?vue-component';
import ResetIcon from '../assets/svg/icon-reset-2.svg?vue-component';
import ClickIcon from '../assets/svg/flashcards/click-hand.svg?vue-component';
import ArrowIcon from '../assets/svg/icon-flashcards-arrow.svg?vue-component';
import LeftRightArrow from '../assets/svg/flashcards/white-left-arrow.svg?vue-component';
import global from '../mixins/global';
import examMixin from '../mixins/exams';

export default {
  components: {
    CardContent,
    LineProgress,
    ResetIcon,
    ClickIcon,
    ShuffleIcon,
    ArrowIcon,
    Tooltip,
    LeftRightArrow,
  },
  mixins: [global, examMixin],
  props: {
    mode: { type: String, required: false, default: 'study' },
    deck: { type: Object, required: true, default: () => ({}) },
    loginEmail: { type: Boolean },
    signupEmail: { type: Boolean },
    forgottenPassword: { type: Boolean },
  },
  data() {
    return {
      promises: [],
      activeIndex: this.mode === 'review' && this.$store.state.auth.user ? this.deck?.results?.studyScore?.reviewedCount : (this.deck.lastReviewedCard || 0),
      flip: false,
      answers: {},
      review: {},
      progress: ((this.mode === 'review' && this.$store.state.auth.user ? this.deck?.results?.studyScore?.reviewedCount + 1 : 1) / this.deck.cards.length) * 100,
      skippedCardsProgress: (1 / this.deck.cards.length) * 100,
      formFlip: false,
      allowCard: false,
      disableRightArrow: false,
      disableLeftArrow: true,
      deckCardsCount: 0,
      progressFirstCard: ((1 / this.deck.cards.length) * 100),
      showFlipMessage: false,
      reviewedCards: [],
      cloneCards: JSON.parse(JSON.stringify(this.deck.cards)),
      currentReviewPosition: 1,
      disableShuffle: false,
      submitting: false,
    };
  },
  computed: {
    activeCard() {
      return this.cloneCards[this.activeIndex];
    },
    showLoginOrSignup() {
      return (this.progress >= 50 || this.skippedCardsProgress >= 50) && !this.$store.state.auth.user && !this.allowCard && this.mode === 'study';
    },
  },
  watch: {
    // eslint-disable-next-line func-names
    '$store.state.auth.user': async function () {
      if (Object.entries(this.answers).length > 0) {
        await this.submitAnswers(this.answers);
      }
    },
    progress() {
      this.allowCard = false;
    },
  },
  created() {
    if (this.deck.cardCount <= 5) {
      this.allowCard = true;
    }
  },
  beforeUpdate() {
    this.deckCardsCount = this.cloneCards.length;

    if (this.deckCardsCount === 1) {
      this.disableLeftArrow = true;
      this.disableRightArrow = true;
    }
  },
  beforeDestroy() {
    if (this.mode === 'review' && this.progress === 100) {
      localStorage.removeItem(`flashcards_${this.deck.id}`);
    }
  },
  mounted() {
    if (this.mode === 'review') {
      if (!this.$store.state.auth.user) {
        this.progress = ((this.deck.lastReviewedCard + 1) / this.deck.cards.length) * 100;
      }

      if (JSON.parse(localStorage.getItem(`flashcards_${this.deck.id}`))) {
        const cards = JSON.parse(localStorage.getItem(`flashcards_${this.deck.id}`));
        const shuffledCards = [];

        cards.forEach((card) => {
          shuffledCards.push(this.deck.cards.find((deckCard) => deckCard.id === card));
        });

        this.cloneCards = shuffledCards;
      }
    }

    if (this.activeIndex > 0) {
      this.disableLeftArrow = false;

      for (let i = 0; i < this.activeIndex; i += 1) {
        this.reviewedCards.push(this.deck.cards[i].id);
      }
    }

    this.currentReviewPosition = this.reviewedCards.length;
    this.disableShuffle = this.reviewedCards.length + 1 === this.deck.cards.length;
    this.deckCardsCount = this.cloneCards.length;
    this.flipMessage();

    window.addEventListener('keydown', this.preventEvent);
    window.addEventListener('keyup', this.pressKeys);

    this.$once('hook:beforeDestroy', () => {
      window.removeEventListener('keydown', this.preventEvent);
      window.removeEventListener('keyup', this.pressKeys);
    });

    this.$root.$on('login', () => {
      this.formFlip = false;
    });

    this.$nextTick(() => this.renderMathJax());

    if (this.progress > this.progressFirstCard && this.progress <= 100) {
      this.$root.$emit('start-test');
    }
  },
  methods: {
    renderMathJax() {
      if (window.MathJax) {
        window.MathJax.Hub.Config({
          tex2jax: {
            inlineMath: [['$', '$'], ['\\(', '\\)']],
            processEscapes: true,
          },
          'HTML-CSS': {
            linebreaks: { automatic: true, width: 'container' },
          },
          SVG: {
            linebreaks: { width: '10000px' },
          },
          jax: ['input/TeX', 'output/SVG'],
        });
        window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub]);
      }
    },
    preventEvent(event) {
      if (this.$store.state.modals.opened.find((m) => m.name === 'login-or-signup')) {
        return;
      }
      if (!this.showLoginOrSignup) {
        if (
          (this.mode === 'study' && [1, 2, 3].includes(+event.key))
          || (this.mode === 'review' && ['ArrowRight', 'ArrowLeft'].includes(event.key))
          || (['review', 'study'].includes(this.mode) && event.key === ' ')
        ) {
          event.preventDefault();
        }

        if (this.mode === 'study' && [1, 2, 3].includes(+event.key)) {
          let currentBtn = '';

          if (+event.key === 1) {
            currentBtn = 'btn-danger-light';
          } else if (+event.key === 2) {
            currentBtn = 'btn-light-shade';
          } else {
            currentBtn = 'btn-primary-light';
          }

          document.getElementsByClassName(currentBtn)[0].classList.add('active');
        }

        if (this.mode === 'review' && ['ArrowRight', 'ArrowLeft'].includes(event.key)) {
          let currentArrow = '';

          if (event.key === 'ArrowRight') {
            currentArrow = 'caret-right';
          } else {
            currentArrow = 'caret-left';
          }

          document.getElementsByClassName(currentArrow)[0].classList.add('active');
        }
      }
    },
    pressKeys(event) {
      if (this.$store.state.modals.opened.find((m) => m.name === 'login-or-signup')) {
        return;
      }
      if (event.key === ' ') {
        this.flip = !this.flip;
      }

      if (!this.showLoginOrSignup) {
        if (this.mode === 'study' && [1, 2, 3].includes(+event.key)) {
          let currentBtn = '';

          if (+event.key === 1) {
            currentBtn = 'btn-danger-light';
          } else if (+event.key === 2) {
            currentBtn = 'btn-light-shade';
          } else {
            currentBtn = 'btn-primary-light';
          }

          document.getElementsByClassName(currentBtn)[0].click();
          document.getElementsByClassName(currentBtn)[0].classList.remove('active');
        }

        if (this.mode === 'review' && ['ArrowRight', 'ArrowLeft'].includes(event.key)) {
          const direction = event.key === 'ArrowRight' ? 'right' : 'left';
          let currentArrow = '';

          if (event.key === 'ArrowRight') {
            currentArrow = 'caret-right';
          } else {
            currentArrow = 'caret-left';
          }

          this.switchCards(direction);
          document.getElementsByClassName(currentArrow)[0].classList.remove('active');
        }
      }
    },
    async reset() {
      const confirm = await this.$store.dispatch('modals/open', {
        name: 'confirm',
        title: 'Are you sure you wish to reset the deck?',
        customClass: 'flashcards-modal',
        message: 'This action will permanently delete all of your progress.',
        confirm: 'Reset Deck',
        confirmBtnClass: 'btn-danger-light',
        cancel: 'Go Back',
        cancelBtnClass: 'btn-primary',
        reverseButtonsOrder: true,
      });
      if (confirm) {
        if (this.$store.state.auth.user) {
          await this.$axios.$delete(`/flashcards/${this.deck.slug}/reset`);
          this.answers = [];
          this.activeIndex = 0;
          this.progress = (1 / this.deck.cards.length) * 100;
          const deck = await this.$axios.$get(`/flashcards/${this.$route.params.slug}/study`);
          // eslint-disable-next-line vue/no-mutating-props
          this.deck.cards = deck.cards;
          this.cloneCards = deck.cards;
        } else {
          this.answers = [];
          this.activeIndex = 0;
          this.progress = (1 / this.deck.cards.length) * 100;
        }
      }
    },
    shuffle() {
      if (this.mode === 'review') {
        const reviewedCards = this.cloneCards.filter((card) => Object.values(this.reviewedCards).includes(card.id));

        // recursive function so that the active card after the shuffle is not the same as the previous one
        const remainingCards = () => {
          const currentRemainingCards = this.cloneCards.filter((card) => !Object.values(this.reviewedCards).includes(card.id)).sort(() => Math.random() - 0.5);

          if (this.activeCard.id === currentRemainingCards[0].id) {
            return remainingCards();
          }

          return currentRemainingCards;
        };

        this.cloneCards = [...reviewedCards, ...remainingCards()];

        localStorage.setItem(`flashcards_${this.deck.id}`, JSON.stringify(this.cloneCards.map((card) => card.id)));
      } else {
        const reviewedCards = this.cloneCards.filter((card) => Object.keys(this.answers).includes(card.id.toString()));

        // recursive function so that the active card after the shuffle is not the same as the previous one
        const remainingCards = () => {
          const currentRemainingCards = this.cloneCards.filter((card) => !Object.keys(this.answers).includes(card.id.toString())).sort(() => Math.random() - 0.5);

          if (this.activeCard.id === currentRemainingCards[0].id) {
            return remainingCards();
          }

          return currentRemainingCards;
        };

        this.cloneCards = [...reviewedCards, ...remainingCards()];
      }
      this.flip = false;
    },
    async nextQuestion(answer) {
      if (!this.showLoginOrSignup) {
        this.$el.querySelector('.flashcard').style.transition = 'none';
        const currentOrder = this.activeIndex + 1;

        this.answers[this.activeCard.id] = answer;
        if (Object.keys(this.answers).length > 0 && Object.keys(this.answers).length < this.deck.cardCount) {
          this.$root.$emit('start-test');
        }

        if (this.$store.state.auth.user) {
          this.promises.push(this.submitAnswers({ [this.activeCard.id]: answer }));
        }

        if (currentOrder === this.deck.cards.length) {
          this.submitting = true;
          await Promise.all(this.promises);
          return this.$router.push(`/flashcards/${this.deck.slug}/checkpoint/`);
        }

        if (currentOrder < this.deck.cards.length) {
          this.activeIndex = currentOrder;
        }

        if (this.flip) {
          this.flip = false;
        }

        setTimeout(() => {
          this.$el.querySelector('.flashcard').style.transition = 'transform 0.25s';
        }, 300);

        this.progress = ((Object.values(this.answers).length + 1) / this.deck.cards.length) * 100;
        this.disableShuffle = Object.values(this.answers).length + 1 === this.deck.cards.length;
        document.activeElement.blur();
      }

      return true;
    },
    async switchCards(direction) {
      this.showFlipMessage = false;

      if (!this.showLoginOrSignup) {
        this.$el.querySelector('.flashcard').style.transition = 'none';

        if (direction === 'left') {
          if (this.disableLeftArrow) {
            return;
          }

          this.disableRightArrow = false;

          if (this.activeIndex - 1 >= 0) {
            this.activeIndex -= 1;
          }

          if (this.activeIndex === 0) {
            this.disableLeftArrow = true;
          }

          this.currentReviewPosition -= 1;
        } else if (direction === 'right') {
          const { id } = this.activeCard;
          this.review[this.activeCard.id] = this.activeCard.id;
          if (Object.keys(this.review).length > 0 && Object.keys(this.review).length < this.deck.cardCount) {
            this.$root.$emit('start-test');
          }
          if (this.disableRightArrow) {
            return;
          }

          if (!Object.values(this.reviewedCards)[this.activeIndex] && (this.reviewedCards.length + 1 < this.deck.cards.length || this.currentReviewPosition + 1 === this.deck.cards.length)) {
            this.reviewedCards.push(this.activeCard.id);
            this.promises.push(this.submitReviewedCard(id));
          }

          this.currentReviewPosition += 1;
          this.disableLeftArrow = false;

          if (this.activeIndex === this.deck.cards.length - 1) {
            this.disableLeftArrow = true;
            this.disableRightArrow = true;
            await Promise.all(this.promises);
            // eslint-disable-next-line consistent-return
            return this.$router.push(`/flashcards/${this.deck.slug}/checkpoint/?review=1`);
          }
          if (this.activeIndex + 1 <= this.deck.cards.length - 1) {
            this.activeIndex += 1;
          }
        }
        if (this.flip) {
          this.flip = false;
        }

        setTimeout(() => {
          this.$el.querySelector('.flashcard').style.transition = 'transform 0.25s';
        }, 300);

        this.progress = ((this.currentReviewPosition + 1) / this.deck.cards.length) * 100;
        this.disableShuffle = this.reviewedCards.length + 1 === this.deck.cards.length || Object.values(this.reviewedCards)[this.activeIndex];
      }
    },
    swipeCards(direction) {
      if (this.mode === 'review') {
        this.switchCards(direction);
      }
    },
    cardFlip(e) {
      if (e.target.classList.contains('image-lightbox') || e.target.tagName === 'IMG') {
        return;
      }

      if (!this.showLoginOrSignup || this.formFlip) {
        this.flip = !this.flip;
      }

      this.$nextTick(() => this.renderMathJax());
    },
    loginSignupHandler(form) {
      if (form === 'signup') {
        this.formFlip = true;
      } else {
        this.formFlip = false;
      }

      this.$emit(form);
      this.$emit('reset-form');
    },
    async submitAnswers(answers) {
      return this.$axios.$post(`/flashcards/${this.$route.params.slug}/study`, { answers }, { progress: false });
    },
    async submitReviewedCard(answerId) {
      if (this.$store.state.auth.user) {
        return this.$axios.$post(`/flashcards/${this.$route.params.slug}/review`, { answerId }, { progress: false });
      }
      if (process.client) {
        const review = cookies.get('review_cards');
        let data = {};
        if (review) {
          data = JSON.parse(review);
        }
        data[this.deck.id] = this.activeIndex + 1;
        data = JSON.stringify(data);
        cookies.set('review_cards', data);
      }
      return true;
    },
    flipMessage() {
      const decks = cookies.get('show_flip_message');
      let data = {};
      if (decks) {
        data = JSON.parse(decks);
        if (data[this.deck.id]) {
          this.showFlipMessage = false;
          return;
        }
      }
      this.showFlipMessage = true;
      data[this.deck.id] = true;
      data = JSON.stringify(data);
      cookies.set('show_flip_message', data);
    },
  },
};
</script>

<style lang="scss">
.deck {
  position: relative;
  padding-bottom: var(--spacing-120);

  .flashcard-wrapper {
    position: relative;
  }

  .review-switch {
    text-align: center;

    .caret-right,
    .caret-left {
      position: relative;
      background: none;
      cursor: pointer;
      border-radius: 4px;
      border: 1px solid var(--main-dark-light-4);
      width: 130px;
      height: 45px;
      padding: 13px 0 14px;

      &:disabled {
        cursor: not-allowed;
      }

      &:active:not(:disabled),
      &.active:not(:disabled) {
        border-color: var(--accent);
        background: var(--accent);

        svg path[stroke] {
          stroke: var(--main-light);
        }
      }

      @include bp($bp-desktop) {
        &:hover:not(:disabled) {
          border-color: var(--accent);
          background: var(--accent-light-2);

          svg path[stroke] {
            stroke: var(--accent);
          }

          .tooltip {
            display: block;
            width: 48px;
            height: 48px;
            border-radius: 10px;
            bottom: 117%;
            z-index: 2;
            background: rgba(20, 31, 49, 0.8);

            p {
              font-size: 12px;
              margin: 0;
              color: var(--main-light-shade-1);
            }

            svg path[stroke] {
              stroke: var(--main-light);
            }
          }
        }
      }
    }

    .caret-right {
      margin-left: var(--spacing-16);

      svg {
        transform: rotate(180deg);
      }
    }
  }

  .question,
  .answer {
    position: relative;
    overflow: hidden;
    padding: var(--spacing-30) var(--spacing-50);
    border: 1px solid var(--accent-shade-1);
    border-radius: 10px;
    background: var(--main-light);

    &.mobile {
      display: none;
    }

    .card {
      height: 100%;
    }

    .first-card-hint {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      width: 100%;
      background: var(--accent-shade-1);
      left: 0;
      right: 0;
      bottom: 0;
      height: 35px;
      color: var(--main-light);
      font-size: 14px;
      font-weight: 500;
      text-align: center;
      padding: var(--spacing-8) 0;

      .or {
        margin-left: 2px;
        margin-right: 2px;
      }

      svg {
        margin-right: 6px;
      }
    }

    &:first-of-type {
      .card {
        margin-top: 0;
      }
    }

    &:hover {
      cursor: pointer;
    }
  }

  .flashcard {
    height: 350px;
    position: relative;
    z-index: 2;
    transition: transform 0.25s;
    transform-style: preserve-3d;

    @-moz-document url-prefix() {
      -webkit-backface-visibility: hidden;
      backface-visibility: hidden;
    }

    &.flip {
      transform: rotateX(180deg);
      transition: transform 0.25s;
    }

    &.login-signup {
      >.front,
      >.back {
        cursor: default;
      }
    }

    >.front,
    >.back {
      position: absolute;
      width: 100%;
      height: 100%;
      background: var(--main-light);
      border-radius: 20px;
      border: 1px solid var(--main-light-shade-4);
      -webkit-backface-visibility: hidden;
      backface-visibility: hidden;

      &.forms {
        padding: 49px var(--spacing-50);
      }

      &.login-email {
        padding-top: 41.5px;
        padding-bottom: 41.5px;

        .forms-study {
          height: 267px;
        }
      }

      &.forgotten-password {
        padding: var(--spacing-60) 84px;

        .forms-study {
          height: 230px;
        }
      }

      &.signup-email {
        padding-top: var(--spacing-30);
        padding-bottom: var(--spacing-30);

        .forms-study {
          height: 290px;
        }
      }

      .forms-study {
        display: flex;
        height: 252px;
        gap: var(--spacing-30);

        .left-side {
          display: flex;
          width: 308px;
          flex-direction: column;
          justify-content: center;
          padding: 0 var(--spacing-6);

          h2 {
            color: var(--main-dark-light-3);
            text-align: center;
          }

          .or-sign-up {
            color: var(--main-dark-opacity-48);
            text-align: center;
            margin: 0;
            line-height: 150%;

            a {
              font-weight: 500;
            }

            span {
              color: var(--link-color);
              cursor: pointer;
            }
          }
        }

        .login-signup-terms-mobile {
          display: none;
        }
      }
    }

    >.front {
      display: block;
      overflow: hidden;
      z-index: 1;
    }

    >.back {
      transform: rotateX(180deg);
    }
  }

  .line-progress {
    margin-top: var(--spacing-12);
    padding: 0;
    border-radius: 50%;

    &.disable .line-progress-bar {
      background: var(--main-light-shade-3);

      span {
        background: var(--main-light-shade-4);
      }
    }

    .line-progress-bar {
      height: 7px;
      width: 100%;

      span {
        background: var(--accent-shade-1);
      }
    }
  }

  .progress-actions {
    display: flex;
    height: 35px;
    margin-top: var(--spacing-6);
    align-items: center;
    justify-content: space-between;

    .group-button {
      .shuffle {
        width: 25px;
      }

      .reset {
        width: 22px;
      }

      button {
        position: relative;
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;

        &:not(:disabled):not(:active):hover {
          svg path[fill] {
            fill: var(--main-dark-light-4);
          }
        }

        &:active {
          svg path[fill] {
            fill: var(--main-dark-light-3);
          }
        }

        &:disabled {
          cursor: not-allowed;

          svg path[fill] {
            fill: var(--main-light-shade-3);
          }
        }

        &:first-of-type {
          margin-right: var(--spacing-16);
        }

        &:hover .tooltip {
          display: block;
          z-index: 2;
          width: 85px;
          padding: 15px 20px;
          line-height: 130%;
          font-size: 14px;
          background: rgba(20, 31, 49, 0.8);
        }
      }
    }

    .cards-number {
      color: #B0B3B9;
      font-size: 14px;
      font-weight: 600;
      line-height: 100%;
    }
  }

  .flashcard-actions {
    text-align: center;

    p {
      margin: 0 0 var(--spacing-20);
      font-size: 18px;
      line-height: 100%;
      letter-spacing: 0.36px;
    }

    .group-button {
      button {
        position: relative;
        margin-right: 15px;
        width: 130px;
        height: 45px;
        padding: 12.946px 0 13.809px 0;
        font-size: 16px;
        font-weight: 600;
        text-transform: capitalize;

        .tooltip {
          border-radius: 10px;
        }

        &:last-of-type {
          margin-right: 0;
        }

        &:not(:disabled):active {
          color: var(--main-light);
        }

        &.btn-danger-light {
          border-color: var(--main-red);
          color: var(--main-red);

          @include bp($bp-desktop) {
            &:not(:disabled):not(:active):hover {
              background: var(--main-red-light-2);
            }
          }

          &.active {
            border-color: var(--main-red);
            background-color: var(--main-red);
            color: var(--main-light);
          }
        }

        &.btn-primary-light {
          border-color: var(--accent);
          color: var(--accent);

          @include bp($bp-desktop) {
            &:not(:disabled):not(:active):hover {
              background: var(--accent-light-2);
            }
          }

          &.active {
            background-color: var(--accent);
            border-color: var(--accent);
            color: var(--main-light);
          }
        }

        &.btn-light-shade {
          border-color: var(--main-dark-light-4);
          color: var(--main-dark-light-4);

          @include bp($bp-desktop) {
            &:not(:disabled):not(:active):hover {
              background: var(--main-light-shade-3);
            }
          }

          &.active {
            background-color: var(--main-dark-light-4);
            border-color: var(--main-dark-light-4);
            color: var(--main-light);
          }
        }

        &:disabled {
          background: var(--main-light-shade-2);
          color: var(--main-light-shade-4);
          border-color: var(--main-light-shade-4);
          opacity: 1;
        }

        &:hover .tooltip {
          display: block;
          z-index: 2;
          width: 48px;
          height: 48px;
          padding: 11px var(--spacing-8);

          p {
            font-size: 12px;
            margin: 0;

            &:first-of-type {
              margin-bottom: 3px;
            }
          }
        }
      }
    }
  }
}

@include bp($bp-laptop) {
  .deck {
    .flashcard {
      >.front {
        &.forms {
          padding: 47px 46.5px;

          .forms-study {
            height: 256px;
          }
        }
      }

      >.back {
        &.forms {
          padding: 31.5px 46.5px;

          .forms-study {
            height: 287px;
          }
        }
      }

      >.front,
      >.back {
        border-radius: 10px;

        &.forms {
          .forms-study {
            .submit {
              height: 52px;
              padding: 15px 0 var(--spacing-16) 0;
            }

            .left-side {
              display: flex;
              width: 215px;
              padding: 0;

              h2 {
                font-size: 18px;
              }
            }
          }
        }
      }
    }
  }
}

@include bp($bp-mobile) {
  .deck {
    &.study {
      padding-bottom: 0;
    }

    .review-switch {
      margin-top: var(--spacing-12);

      button:hover {
        .caret-left,
        .caret-right {
          width: 148px;
          height: 52px;
        }
      }
    }

    .flashcard {
      &.flip {
        transform: rotateY(180deg);
      }

      >.front {
        .first-card-hint {
          .or {
            display: none;
          }
        }

        &.forgotten-password {
          padding: var(--spacing-60) 188px;
        }
      }

      >.back {
        transform: rotateY(180deg);
      }
    }

    .question,
    .answer {
      padding: var(--spacing-30) var(--spacing-40) var(--spacing-40);
    }

    .question {
      &.desktop {
        display: none;
      }

      &.mobile {
        display: block;
      }

      .card {
        padding: var(--spacing-30) var(--spacing-40) var(--spacing-lg);
        height: 100%;
        margin-top: var(--spacing-20);

        .question-content {
          height: 100%;
          flex-direction: column;

          p {
            text-align: center;
            font-size: 21px !important;

            span {
              font-size: 21px !important;
            }
          }
        }
      }
    }

    .progress-actions {
      height: 28px;

      .group-button {
        .shuffle {
          width: 20px;
        }

        .reset {
          width: 17px;
        }

        .shuffle,
        .reset {
          &:hover .tooltip {
            display: none;
          }
        }
      }
    }

    .flashcard-actions {
      margin: var(--spacing-16) 0 var(--spacing-100) !important;

      .group-button {
        button:hover .tooltip {
          display: none;
        }

        .btn-danger-light {
          order: 3;
        }

        .btn-light-shade {
          order: 2;
        }

        .btn-primary-light {
          order: 1;
        }
      }
    }
  }
}

@include bp($bp-phone) {
  .deck {
    .flashcard {
      height: 500px;

      >.front,
      >.back {
        &.forms {
          padding: 66.5px 19px;

          &.signup-email {
            padding: 35px 19px;
          }

          .forms-study {
            height: 367px;
            flex-direction: column;
            gap: 0;

            .login-signup-terms-mobile {
              display: flex;
              flex-direction: column;
              text-align: center;

              .or-sign-up {
                margin: var(--spacing-8) 0 var(--spacing-22);
              }

              .terms {
                display: block;
                font-size: 14px;
                margin: 0;

                &.login-form,
                &.signup-form {
                  display: none;
                }
              }
            }

            .arrow-flashcards-study {
              margin-bottom: var(--spacing-22);

              h1 {
                font-size: 21px;
              }
            }

            .left-side {
              width: 287px;
              align-self: center;
              align-items: center;
              margin-bottom: var(--spacing-32);
              padding: 0;

              h2 {
                font-size: 18px;
                margin: 0;
              }

              .or-sign-up {
                display: none;
              }
            }
          }

          &.forgotten-password {
            padding: var(--spacing-120) 19px;

            .login-signup-terms-mobile {
              display: none;
            }
          }
        }
      }

      .card {
        padding: 0;

        .question-content,
        .answer-content {
          font-size: 21px;
        }
      }
    }

    .flashcard-actions.flashcard-actions {
      p {
        font-size: 16px;
        color: var(--main-dark-light-3);
      }

      .group-button {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-12);

        button {
          width: 100%;
          height: 52px;
          margin: 0;
        }
      }
    }
  }
}
</style>
